'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

// metadata removed - client component

const capabilities = [
  {
    icon: 'fas fa-sitemap',
    titleEs: 'Arquitectura de Software',
    titleFr: 'Architecture Logicielle',
    descriptionEs: 'Diseñamos arquitecturas escalables y mantenibles adaptadas a los requerimientos de tu negocio, eligiendo las tecnologías más adecuadas para cada caso.',
    descriptionFr: 'Nous concevons des architectures évolutives et maintenables adaptées aux exigences de votre entreprise, en choisissant les technologies les plus appropriées pour chaque cas.',
  },
  {
    icon: 'fas fa-mobile-alt',
    titleEs: 'Aplicaciones Móviles',
    titleFr: 'Applications Mobiles',
    descriptionEs: 'Desarrollo de apps nativas y multiplataforma para iOS y Android con experiencias de usuario fluidas, intuitivas y alineadas a tu marca.',
    descriptionFr: 'Développement d\'applications natives et multiplateformes pour iOS et Android avec des expériences utilisateur fluides, intuitives et alignées sur votre marque.',
  },
  {
    icon: 'fas fa-globe',
    titleEs: 'Plataformas Web',
    titleFr: 'Plateformes Web',
    descriptionEs: 'Sistemas web robustos, portales corporativos, e-commerce y aplicaciones SaaS construidos con tecnologías modernas y enfoque en rendimiento.',
    descriptionFr: 'Systèmes web robustes, portails d\'entreprise, e-commerce et applications SaaS construits avec des technologies modernes et axés sur les performances.',
  },
  {
    icon: 'fas fa-plug',
    titleEs: 'Integraciones y APIs',
    titleFr: 'Intégrations et APIs',
    descriptionEs: 'Conectamos tus sistemas existentes con nuevas soluciones mediante APIs REST, GraphQL e integraciones con plataformas de terceros.',
    descriptionFr: 'Nous connectons vos systèmes existants à de nouvelles solutions via des API REST, GraphQL et des intégrations avec des plateformes tierces.',
  },
  {
    icon: 'fas fa-robot',
    titleEs: 'Automatización con IA',
    titleFr: 'Automatisation avec IA',
    descriptionEs: 'Incorporamos inteligencia artificial y machine learning en tus procesos para automatizar tareas, extraer insights y mejorar la toma de decisiones.',
    descriptionFr: 'Nous intégrons l\'intelligence artificielle et le machine learning dans vos processus pour automatiser les tâches, extraire des insights et améliorer la prise de décision.',
  },
  {
    icon: 'fas fa-sync-alt',
    titleEs: 'Mantenimiento y Evolución',
    titleFr: 'Maintenance et Évolution',
    descriptionEs: 'Soporte continuo, actualizaciones de seguridad, mejoras de rendimiento y evolución funcional para mantener tu software siempre competitivo.',
    descriptionFr: 'Support continu, mises à jour de sécurité, améliorations des performances et évolution fonctionnelle pour maintenir votre logiciel toujours compétitif.',
  },
];

const techStack = ['React / Next.js', 'Node.js', 'Python', 'React Native', 'PostgreSQL', 'Docker', 'AWS / Azure', 'REST APIs'];

export default function DesarrolloSoftwarePage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const s = t?.services;
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />
      <section className="relative overflow-hidden" style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" /></div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" /></div>
        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#servicios" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbServices}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{isFr ? 'Développement Logiciel' : 'Desarrollo de Software'}</span>
          </nav>
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2" style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fas fa-code" /><span>{isFr ? 'SOLUTIONS SUR MESURE' : 'SOLUCIONES A MEDIDA'}</span>
              </div>
              <h1 className="text-white font-bold mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {isFr ? 'Développement' : 'Desarrollo de'}<br /><span style={{ color: '#1AEF0F' }}>{isFr ? 'Logiciel et Apps' : 'Software y Apps'}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {isFr ? 'Nous construisons des solutions numériques sur mesure qui résolvent de vrais problèmes d\'entreprise. Des applications mobiles aux plateformes d\'entreprise complexes.' : 'Construimos soluciones digitales a medida que resuelven problemas reales de negocio. Desde aplicaciones móviles hasta plataformas empresariales complejas.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://calendar.app.google/qrMMuz1WHJJ4eoab8" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center overflow-hidden" style={{ border: '1px solid #1AEF0F', padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', color: '#1AEF0F', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">{isFr ? 'Demander un Devis' : 'Cotizar Proyecto'}</span>
                  <span className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ backgroundColor: '#1AEF0F', zIndex: 0 }} />
                </a>
                <Link href="/#servicios" className="inline-flex items-center gap-2 text-white hover:text-[#1AEF0F] transition-colors" style={{ fontSize: '15px', padding: '14px 0' }}>
                  <i className="fas fa-arrow-left text-sm" />{isFr ? 'Voir tous les services' : 'Ver todos los servicios'}
                </Link>
              </div>
            </div>
            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {[
                { value: isFr ? 'Agile' : 'Ágil', labelEs: 'Metodología Scrum', labelFr: 'Méthodologie Scrum' },
                { value: '+30', labelEs: 'Apps desarrolladas', labelFr: 'Apps développées' },
                { value: 'Full', labelEs: 'Stack development', labelFr: 'Stack development' },
                { value: 'IA', labelEs: 'Integración nativa', labelFr: 'Intégration native' },
              ]?.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '28px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>{stat?.value}</div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>{isFr ? stat?.labelFr : stat?.labelEs}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="mb-12">
            <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.capabilitiesTitle}</h2>
            <p style={{ fontSize: '16px', color: '#505050', maxWidth: '600px', lineHeight: '1.6' }}>{isFr ? 'Nous couvrons le cycle complet de développement : de la conception de l\'architecture à la maintenance post-lancement, avec des équipes spécialisées à chaque étape.' : 'Cubrimos el ciclo completo de desarrollo: desde el diseño de arquitectura hasta el mantenimiento post-lanzamiento, con equipos especializados en cada etapa.'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {capabilities?.map((cap, i) => (
              <div key={i} className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]" style={{ borderBottom: '1px solid #E2E2E2', borderRight: i % 3 !== 2 ? '1px solid #E2E2E2' : 'none' }}>
                <div className="mb-5 flex items-center justify-center" style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${cap?.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-3" style={{ fontSize: '17px', fontFamily: "'Titillium Web', sans-serif" }}>{isFr ? cap?.titleFr : cap?.titleEs}</h3>
                <p style={{ fontSize: '14px', color: '#505050', lineHeight: '1.6' }}>{isFr ? cap?.descriptionFr : cap?.descriptionEs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '60px', paddingBottom: '80px', backgroundColor: '#f5f5f5' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <h2 className="font-bold text-black mb-10" style={{ fontSize: 'clamp(22px, 2vw, 30px)', fontFamily: "'Titillium Web', sans-serif" }}>
            {isFr ? 'Stack Technologique' : 'Stack Tecnológico'}
          </h2>
          <div className="flex flex-wrap gap-3">
            {techStack?.map((tech, i) => (
              <div key={i} className="px-5 py-3 bg-white" style={{ border: '1px solid #E2E2E2', fontSize: '14px', fontWeight: '600', color: '#111' }}>{tech}</div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '70px', paddingBottom: '70px', backgroundColor: '#1AEF0F' }}>
        <div className="max-w-[1200px] mx-auto px-[35px] flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-black font-bold m-0" style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.3' }}>
            {isFr ? 'Vous avez un projet logiciel en tête ?' : '¿Tienes un proyecto de software en mente?'}
          </p>
          <Link href="/contact" className="group relative inline-flex items-center overflow-hidden border border-black text-black bg-transparent flex-shrink-0" style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', transition: 'all 300ms ease-out' }}>
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{s?.ctaBtnContact}</span>
            <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
